const DATABASE_USER_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Cookie set by the browser with posthog.get_distinct_id(). */
export const POSTHOG_BROWSER_DISTINCT_ID_COOKIE = "ap_ph_distinct_id";

/** Set after the httpOnly visitor id has been merged into a Clerk person. */
export const POSTHOG_VISITOR_LINKED_COOKIE = "ap_ph_visitor_linked";

const MAX_DISTINCT_ID_LENGTH = 200;

export function isDatabaseUserId(value: string | null | undefined): boolean {
  const trimmed = value?.trim() ?? "";
  return DATABASE_USER_ID_RE.test(trimmed);
}

function normalizedDistinctId(value: string | null | undefined): string {
  return value?.trim() ?? "";
}

/**
 * PostHog distinct_id must match client posthog.identify(clerkUserId).
 * Database user ids are not a valid Clerk identity. Anonymous ids (the
 * PostHog device id or the app visitor id) are valid until that merge.
 */
export function resolvePostHogDistinctId(input: {
  clerkUserId?: string | null;
  anonymousDistinctId?: string | null;
}): string | null {
  const clerkUserId = normalizedDistinctId(input.clerkUserId);
  if (
    clerkUserId &&
    !clerkUserId.startsWith("pending:") &&
    !isDatabaseUserId(clerkUserId)
  ) {
    return clerkUserId;
  }

  const anonymousDistinctId = normalizedDistinctId(input.anonymousDistinctId);
  if (!anonymousDistinctId || anonymousDistinctId.startsWith("pending:")) {
    return null;
  }
  if (anonymousDistinctId.length > MAX_DISTINCT_ID_LENGTH) {
    return null;
  }
  return anonymousDistinctId;
}

export function readPostHogBrowserDistinctId(
  value: string | null | undefined,
): string | null {
  if (!value) {
    return null;
  }
  let decoded = value.trim();
  try {
    decoded = decodeURIComponent(decoded).trim();
  } catch {
    return null;
  }
  if (
    !decoded ||
    decoded.startsWith("pending:") ||
    decoded.length > MAX_DISTINCT_ID_LENGTH
  ) {
    return null;
  }
  return decoded;
}

export function planVisitorPostHogLink(input: {
  clerkUserId?: string | null;
  visitorId?: string | null;
  linkedClerkUserId?: string | null;
}): { clerkUserId: string; visitorId: string } | null {
  const clerkUserId = resolvePostHogDistinctId({
    clerkUserId: input.clerkUserId,
  });
  const visitorId = normalizedDistinctId(input.visitorId);
  if (!clerkUserId || !visitorId || visitorId === clerkUserId) {
    return null;
  }
  if (normalizedDistinctId(input.linkedClerkUserId)) {
    return null;
  }
  return { clerkUserId, visitorId };
}
